import {Component, OnInit} from '@angular/core';
import {BlockUI} from 'primeng/blockui';
import {BlockableDivComponent} from '../../../components/form/blockable-div/blockable-div.component';
import {RestApiService} from '../../../services/api/rest-api.service';
import {ActivatedRoute, Router} from '@angular/router';
import {RoutingConstants} from '../../../models/routing/routing-constants';
import {ToastService} from '../../../services/toast.service';
import {DialogService} from 'primeng/dynamicdialog';
import {MediaSelectorComponent} from '../../../components/media-manager/media-selector/media-selector.component';
import {InputComponent} from '../../../components/form/input/input.component';
import {
  Language,
  Movie,
  MovieCalculationMode,
  MovieAvailability,
  MovieAvailabilityStatus,
  MovieDetails,
  MovieGenre, MovieGenreAssociations,
  MovieRights,
  MovieStatus, SkyCoExclusiveSoldStatus, SkyMovieRights, Tentative,
  Window
} from '../../../models/vision/movie';
import {DropdownComponent} from '../../../components/form/dropdown/dropdown.component';
import {DatetimeComponent} from '../../../components/form/datetime/datetime.component';
import {TableModule} from 'primeng/table';
import {CheckboxComponent} from '../../../components/form/checkbox/checkbox.component';
import {Button} from 'primeng/button';
import {Ripple} from 'primeng/ripple';
import {DatePipe} from '@angular/common';
import {FormGenreComponent} from '../genres/form-genre/form-genre.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MultiSelect} from 'primeng/multiselect';
import {FormsModule} from '@angular/forms';
import {PojoPipe} from '../../../pipes/pojo.pipe';
import {toDropdownList} from "../../../utils/enum-utils";
import {Routing} from "../../../models/routing/routing";
import {RestApiError} from '../../../services/api/rest-api-error';
import {ActionButtonComponent} from '../../../components/form/action-buttons/action-button.component';
import {ConfirmDialog} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {Tooltip} from 'primeng/tooltip';
import {Popover} from 'primeng/popover';
import {Dialog} from 'primeng/dialog';
import {Textarea} from 'primeng/textarea';
import {windowLabel, WindowLabelPipe} from '../../../pipes/window-label.pipe';


@Component({
  selector: 'app-movie-form',
  imports: [
    BlockUI,
    BlockableDivComponent,
    MediaSelectorComponent,
    InputComponent,
    DropdownComponent,
    DatetimeComponent,
    TableModule,
    CheckboxComponent,
    Button,
    Ripple,
    DatePipe,
    MultiSelect,
    FormsModule,
    PojoPipe,
    ActionButtonComponent,
    ConfirmDialog,
    Tooltip,
    Popover,
    Dialog,
    Textarea,
    WindowLabelPipe
  ],
  templateUrl: './movie-form.component.html',
  styleUrl: './movie-form.component.scss'
})
@UntilDestroy()
export class MovieFormComponent implements OnInit {
  readonly Window = Window;
  readonly MovieAvailabilityStatusList: { label: string, value: string }[] = toDropdownList(MovieAvailabilityStatus);
  readonly MovieAvailabilityWindowList: { label: string, value: string }[] = toDropdownList(Window);
  readonly MovieAvailabilityLimitedWindowList: {
    label: string,
    value: string
  }[] = toDropdownList(Window).filter(w => w.value === Window.Svod
    || w.value === Window.FreeTv
    || w.value === Window.LibrarySvod
    || w.value === Window.LibraryFreeTv);
  readonly MovieAvailabilityRightsList: { label: string, value: string }[] = toDropdownList(MovieRights);
  readonly MovieCalculationModeList: { label: string, value: MovieCalculationMode }[] = [
    {label: 'Campi liberi', value: MovieCalculationMode.FreeFields},
    {label: 'Applica formule', value: MovieCalculationMode.AutoFormulaV01},
  ];
  readonly MovieTentativeList: { label: string, value: string }[] = [
    {label: '-', value: Tentative.NoValue},
    {label: 'T', value: Tentative.T},
    {label: 'C', value: Tentative.C},
  ];
  readonly SkyMovieRightsList: { label: string, value: string }[] = toDropdownList(SkyMovieRights);
  readonly skyCoExclusiveSoldStatusList: { label: string, value: string }[] = toDropdownList(SkyCoExclusiveSoldStatus);
  readonly SkyMovieRights = SkyMovieRights;
  readonly MovieAvailabilityStatus = MovieAvailabilityStatus;

  dialogPosition: 'left' | 'right' | 'top' | 'bottom' | 'center' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright' = 'bottomright';

  loading = false;
  movieId: number = 0;

  movie: Movie = new Movie();
  pendingCalculationMode?: MovieCalculationMode;

  editingRow?: MovieAvailability = undefined;
  displayNoteDialog = false;

  get italianDetail(): MovieDetails {
    return this.movie.movieDetails.find(detail => detail.language === Language.Italian)!;
  }

  get englishDetail(): MovieDetails {
    return this.movie.movieDetails.find(detail => detail.language === Language.English)!;
  }

  get isFormulaMode(): boolean {
    return this.movie.calculationMode === MovieCalculationMode.AutoFormulaV01;
  }

  get displayedCalculationMode(): MovieCalculationMode {
    return this.pendingCalculationMode ?? this.movie.calculationMode;
  }

  labelWidth = '120px';

  genres: MovieGenre[] = [];
  subGenres: MovieGenre[] = [];

  movieGenres: number[] = [];
  movieSubGenres: number[] = [];

  constructor(private readonly api: RestApiService,
              private readonly route: ActivatedRoute,
              private readonly router: Router,
              private readonly toastService: ToastService,
              private readonly dialogService: DialogService,
              private readonly confirmationService: ConfirmationService,
  ) {
  }

  async ngOnInit() {
    this.movieId = parseInt(this.route.snapshot.params[RoutingConstants.params.id.substring(1)], 10);
    if (isNaN(this.movieId)) {
      this.movieId = 0;
    }
    this.load();
  }

  async load() {
    try {
      this.loading = true;

      const results = await Promise.all([
        this.loadGenres(),
        this.loadMovie(),
      ]);


      this.movieGenres = this.movie.movieGenreAssociations
        .filter(x => this.genres.map(g => g.movieGenreId).includes(x.movieGenreId))
        .map(x => x.movieGenreId);

      this.movieSubGenres = this.movie.movieGenreAssociations
        .filter(x => this.subGenres.map(g => g.movieGenreId).includes(x.movieGenreId))
        .map(x => x.movieGenreId);

      this.movie.applyAutomations();
    } finally {
      this.loading = false;
    }
  }

  async loadMovie() {
    if (this.movieId) {
      try {
        const response = await this.api.movies.byId(this.movieId);
        this.movie = response.data;
      } catch (error) {
        if (error instanceof RestApiError) {
          if (error.code === 404) {
            this.toastService.error('Film non trovato', 'Il film richiesto non esiste o è stato eliminato.');
            this.router.navigate([Routing.movies.getBasePath()]);
          }
        }
      }
    }
  }

  async loadGenres() {
    const response = await this.api.genres.getGenres();
    const allGenres = response.data;

    this.genres = allGenres
      .filter(genre => !genre.isSubGenre)
      .sort((a, b) => a.name.localeCompare(b.name));

    this.subGenres = allGenres
      .filter(genre => genre.isSubGenre)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async save(action?: 'publish' | 'unpublish') {
    if (action === 'publish') {
      this.movie.published = true;
      this.movie.status = MovieStatus.Published;
      this.movie.publishedAt = new Date();
    } else if (action === 'unpublish') {
      this.movie.published = false;
      this.movie.status = MovieStatus.Draft;
      this.movie.publishedAt = undefined;
    }

    const genreAssociations = this.movieGenres.map(g => {
      return MovieGenreAssociations.create(this.movieId, g);
    });

    const subGenreAssociations = this.movieSubGenres.map(g => {
      return MovieGenreAssociations.create(this.movieId, g);
    });

    this.movie.movieGenreAssociations = [...genreAssociations, ...subGenreAssociations];

    try {
      this.loading = true;
      const response = await this.api.movies.save(this.movie);
      if (response.data) {
        this.toastService.success('Salvato con successo', 'Il film è stato salvato correttamente.');
        this.movie = response.data;
        if (!this.movieId) {
          this.router.navigate([Routing.movies.edit(this.movie.movieId)]);
        }
      } else {
        this.toastService.error('Errore durante il salvataggio', response.statusText);
      }
    } finally {
      this.loading = false;
    }
  }

  obj(item: MovieAvailability): MovieAvailability {
    return item;
  }

  openLink(link: string) {
    if (link) {
      if (!link.startsWith('http://')
        && !link.startsWith('https://')
        && !link.startsWith('//')) {
        link = '//' + link;
      }
      window.open(link, '_blank');
    } else {
      this.toastService.error('Link non valido', 'Il link specificato non è valido.');
    }
  }

  createGenre(type: 'Genre' | 'SubGenre') {
    FormGenreComponent.openDialog(this.dialogService,
      {
        genreId: 0,
        name: '',
        type: type
      }
    )
      .onClose
      .pipe(untilDestroyed(this))
      .subscribe(async (x: MovieGenre) => {
          await this.loadGenres();
          if (type === 'Genre') {
            this.movieGenres.push(x.movieGenreId);
          } else {
            this.movieSubGenres.push(x.movieGenreId);
          }
        }
      );
  }

  calculate() {
    this.movie.applyAutomations();
  }

  updateLtr(newValue: Date | undefined) {
    this.movie.ltr = newValue;
    this.movie.applyAutomations();
  }

  updateCalculationMode(newValue: MovieCalculationMode) {
    if (this.movie.calculationMode === MovieCalculationMode.FreeFields
      && newValue === MovieCalculationMode.AutoFormulaV01) {
      this.pendingCalculationMode = newValue;
      this.dialogPosition = 'center';
      this.confirmationService.confirm({
        key: 'dialog',
        header: 'Conferma applicazione formule',
        message: 'Le formule ricalcoleranno i dati delle finestre e sostituiranno i valori inseriti manualmente. Vuoi continuare?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Applica formule',
        rejectLabel: 'Annulla',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptButtonStyleClass: 'p-button-warning',
        rejectButtonStyleClass: 'p-button-outlined',
        defaultFocus: 'reject',
        closable: false,
        closeOnEscape: false,
        dismissableMask: false,
        accept: () => this.applyCalculationMode(newValue),
        reject: () => this.pendingCalculationMode = undefined,
      });
      return;
    }

    this.applyCalculationMode(newValue);
  }

  private applyCalculationMode(newValue: MovieCalculationMode) {
    this.pendingCalculationMode = undefined;
    this.movie.calculationMode = newValue;
    this.movie.applyAutomations();
  }

  updateMovieRights(row: MovieAvailability, newValue: MovieRights | null) {
    row.movieRights = newValue;
    this.movie.applyAutomations();
  }

  updateMovieMonths(row: MovieAvailability, newValue: number) {
    row.months = Number(newValue);
    if (isNaN(row.months) || row.months < 0) {
      row.months = 0;
    }
    this.movie.applyAutomations();
  }

  updateMovieFrom(row: MovieAvailability, newValue?: Date) {
    row.from = newValue;
    this.movie.applyAutomations();
  }

  updateMovieExpiration(row: MovieAvailability, newValue?: Date) {
    row.expiration = newValue;
  }

  updateFirstTxSky(value: Date | undefined) {
    this.movie.firstTxSky = value;
    this.movie.applyAutomations();
  }

  updateSpecial8m(value: boolean) {
    this.movie.special8M = value;
    this.movie.applyAutomations();
  }

  addAvailability() {
    const maxSequence = this.movie.movieAvailability.map(x => x.sequence).reduce((max, item) => {
      return item > max ? item : max;
    });

    this.movie.movieAvailability.push(new MovieAvailability(
      this.movie.movieAvailability.length == 4 ? Window.FreeTv :
        this.movie.movieAvailability.length == 5 ? Window.LibrarySvod :
          Window.LibraryFreeTv,
      maxSequence + 1));
    this.movie.applyAutomations();
  }

  removeAvailability(availability: MovieAvailability) {
    this.dialogPosition = 'center';

    this.confirmationService.confirm({
      message: `Sei sicuro di voler rimuovere la disponibilità per ${windowLabel(availability.window)}?<br>Questa azione non può essere annullata.`,
      header: 'Conferma rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Rimuovi',
      acceptButtonStyleClass: 'btn p-button-danger',
      rejectLabel: 'Annulla',
      rejectButtonStyleClass: 'p-button-outlined',
      rejectIcon: 'pi pi-times',
      acceptIcon: 'pi pi-trash',
      accept: () => {
        const index = this.movie.movieAvailability.indexOf(availability);
        if (index > -1) {
          this.movie.movieAvailability.splice(index, 1);
        }
        this.movie.applyAutomations();
      },
      key: 'dialog',
    });
  }

  deleteTitle() {
    this.dialogPosition = 'center';
    this.confirmationService.confirm({
      message: 'Sei sicuro di voler eliminare questo film?<br>Questa azione non può essere annullata.',
      header: 'Conferma eliminazione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Elimina',
      acceptButtonStyleClass: 'btn p-button-danger',
      rejectLabel: 'Annulla',
      rejectButtonStyleClass: 'p-button-outlined',
      rejectIcon: 'pi pi-times',
      acceptIcon: 'pi pi-trash',
      accept: async () => {
        try {
          this.loading = true;
          await this.api.movies.deleteById(this.movieId);
          this.toastService.success('Eliminato con successo', 'Il film è stato eliminato correttamente.');
          await this.router.navigate([Routing.movies.getBasePath()]);
        } finally {
          this.loading = false;
        }
      },
      key: 'dialog',
    });
  }

  protected showNoteEditor(movieAvailability: MovieAvailability) {
    this.editingRow = movieAvailability;
    this.displayNoteDialog = true;
  }
}
